import { useRouter } from "next/router";
import { useModalHook } from "../../../../../services/hooks/useModal";
import { useAppSelector } from "../../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../../services/hooks/useTranslationI18";
import { PagesEnum, ReceiptSourceTypeEnum } from "../../../../../utils/enums";
import { getPageLink } from "../../../../../utils/help/navigation/navigation";
import { getTimeFormat12Hour } from "../../../../../utils/help/textPreparation/textPreparation";
import { PaidReceipt } from "../../../../../utils/types";
import { getOrderTypeTranslationKey } from "../PreviousOrdersHelper";

export type UseCurrentOrderProps = {
  receipt: PaidReceipt;
  multipleOrders: boolean;
};

export const useCurrentOrder = (props: UseCurrentOrderProps) => {
  const { receipt, multipleOrders } = props;
  const router = useRouter();
  const { t } = useTranslationI18();
  const { handleModal } = useModalHook();

  const franchise = useAppSelector((state) => state.config.selectedFranchise);
  const unRatedReceiptsIds = useAppSelector(
    (state) => state.receipt.unRatedReceiptsIds
  );
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const bottomSheetModal = useAppSelector(
    (state) => state.globalComponentsToggleSlice.bottomSheetModal
  );

  const rate = t("common:rate");
  const seeMoreDetails = t("common:see-more-details");
  const at = t("common:at");
  const yourCurrentOrder = t("common:your-current-order");
  const order = t("common:order");
  const trackYourOrder = t("common:track-your-order");

  const getTimeInHours = () => {
    if (!multipleOrders) {
      return "";
    }
    const date = new Date(receipt.created_at);
    const options: Intl.DateTimeFormatOptions = {
      hour: "numeric",
      minute: "2-digit",
      hourCycle: "h12",
      timeZone: franchise?.timeZone,
    };

    return at + " " + date.toLocaleTimeString("en-US", options);
  };

  const openFeedbackModal = (receiptId: number, initialStar?: number) => {
    handleModal(true, "FeedBack/FeedBack", {
      componentProps: { receiptId, initialStar },
    });
  };

  const hasFeedback = !unRatedReceiptsIds.includes(receipt.id);

  const payPageLink = getPageLink(router, PagesEnum.Pay, {
    receiptId: receipt.id,
  });

  const orderTypeTranslationKey = getOrderTypeTranslationKey(receipt);
  const formattedTime = getTimeFormat12Hour(receipt.created_at, t);
  const sourceType = receipt.source_type ?? receipt.receiptExtras?.source_type;
  const isCallCenter = sourceType === ReceiptSourceTypeEnum.CALL_CENTER;

  return {
    t,
    receipt,
    rate,
    seeMoreDetails,
    yourCurrentOrder,
    order,
    trackYourOrder,
    getTimeInHours,
    openFeedbackModal,
    hasFeedback,
    restaurantInfo,
    bottomSheetModal,
    payPageLink,
    orderTypeTranslationKey,
    formattedTime,
    isCallCenter,
  };
};
